import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'
import {
  getCurrentUser,
  signIn,
  confirmSignIn,
  signOut,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  // resetPassword,
  //  confirmResetPassword
} from 'aws-amplify/auth'
import type {
  SignInInput,
  ConfirmSignInInput,
  SignInOutput,
  ResetPasswordInput,
  ConfirmResetPasswordInput,
  // ResetPasswordInput,
  // ConfirmResetPasswordInput
} from 'aws-amplify/auth'

import { getEnvBoolean } from '@/utils/env'
import mockAuthService from '@/services/MockAuthService'

const isMockMode = getEnvBoolean('VITE_MOCK_AUTH')

export interface SessionUser {
  username?: string
  signInDetails?: {
    loginId?: string
    authFlowType?: string
  }
  authFlowType?: string
  attributes?: Record<string, string | boolean | undefined>
  [key: string]: unknown
}

export type NextAction = SignInOutput
export type LoginResult =
  | 'loggedIn'
  | 'nextActionRequired'
  | 'passwordResetRequired'
  | 'userNotAuthorizedError'
  | 'tooManyRequestsError'
  | 'unexpectedError'

export type LoginNextStepResult =
  | 'loggedIn'
  | 'nextActionRequired'
  | 'codeMismatchError'
  | 'expiredCodeError'
  | 'MFAMethodNotFoundError'
  | 'enableSoftwareTokenMFAError'
  | 'softwareTokenMFANotFoundError'
  | 'concurrentModificationError'
  | 'passwordResetRequired'
  | 'userNotAuthorizedError'
  | 'tooManyRequestsError'
  | 'unexpectedError'

export const enum LoginStage {
  AwaitingFirstCheck,
  FirstCheck,
  AwaitingLogin,
  LoggingIn,
  AwaitingNextAction,
  SendingNextAction,
  Authenticated,
  LoggingOut,
}

export type ResetPasswordResult =
  | 'done'
  | 'enterCodeSMS'
  | 'enterCodeEmail'
  | 'codeDeliveryFailure'
  | 'forbidden'
  | 'limitExceeded'
  | 'tooManyRequests'
  | 'unexpectedError'

export type ConfirmResetPasswordResult =
  | 'passwordReset'
  | 'codeMismatch'
  | 'expiredCode'
  | 'forbidden'
  | 'invalidPassword'
  | 'limitExceeded'
  | 'tooManyFailedAttempts'
  | 'tooManyRequests'
  | 'unexpectedError'
//TODO - what if token expires?

export const useAuthStore = defineStore('auth', () => {
  const loginStage = ref<LoginStage>(LoginStage.AwaitingFirstCheck)
  const nextAction = ref<SignInOutput['nextStep']>()
  const user = ref<SessionUser | null>(null)

  async function initializeAuth(): Promise<void> {
    console.log('initializing auth')
    loginStage.value = LoginStage.FirstCheck
    try {
      const currentUser = isMockMode ? await mockAuthService.getCurrentUser() : await getCurrentUser()
      if (currentUser) {
        loginStage.value = LoginStage.Authenticated
        if (isMockMode) {
          user.value = currentUser as unknown as SessionUser
        } else {
          const userAttributes = await fetchUserAttributes()
          user.value = {
            ...(currentUser as unknown as SessionUser),
            attributes: userAttributes,
          }
        }
      } else {
        user.value = null
        loginStage.value = LoginStage.AwaitingLogin
      }
      // oxlint-disable-next-line no-unused-vars
    } catch (error) {
      // console.log('User is not authenticated', error)
      try {
        await signOut()
        // oxlint-disable-next-line no-unused-vars
      } catch (e) {
        // console.error('Error during sign out', e)
      }

      user.value = null
      loginStage.value = LoginStage.AwaitingLogin
    }
  }

  async function login(params: SignInInput): Promise<LoginResult> {
    console.log('logging in')
    try {
      const { nextStep } = await signIn(params)
      nextAction.value = nextStep
      console.log(nextStep)
      if (nextStep.signInStep === 'DONE') {
        user.value = (isMockMode ? await mockAuthService.getCurrentUser() : await getCurrentUser()) as unknown as SessionUser
        if (!isMockMode) {
          const userAttributes = await fetchUserAttributes()
          user.value = {
            ...(user.value as unknown as SessionUser),
            attributes: userAttributes,
          }
        }
        loginStage.value = LoginStage.Authenticated
        return 'loggedIn'
      } else {
        loginStage.value = LoginStage.AwaitingNextAction
        return 'nextActionRequired'
      }
    } catch (e: unknown) {
      user.value = null
      if (!(e instanceof Error)) {
        return 'unexpectedError'
      }

      //https://github.com/aws-amplify/amplify-js/blob/75e1c72bf92fe8ae32cdf239d5f4b89d6fd4432a/packages/auth/src/providers/cognito/types/errors.ts
      switch (e.name) {
        case 'NotAuthorizedException':
          return 'userNotAuthorizedError'
        case 'PasswordResetRequiredException':
          return 'passwordResetRequired'
        case 'TooManyRequestsException':
          return 'tooManyRequestsError'
        default:
          return 'unexpectedError'
      }
    }
  }

  async function loginNextStep(parameters: ConfirmSignInInput): Promise<LoginNextStepResult> {
    try {
      const { nextStep } = await confirmSignIn(parameters)
      console.log(nextStep)
      if (nextStep.signInStep === 'DONE') {
        user.value = (isMockMode ? await mockAuthService.getCurrentUser() : await getCurrentUser()) as unknown as SessionUser
        if (!isMockMode) {
          const userAttributes = await fetchUserAttributes()
          user.value = {
            ...(user.value as unknown as SessionUser),
            attributes: userAttributes,
          }
        }
        loginStage.value = LoginStage.Authenticated
        return 'loggedIn'
      } else {
        loginStage.value = LoginStage.AwaitingNextAction
        return 'nextActionRequired'
      }
    } catch (e: unknown) {
      user.value = null
      if (!(e instanceof Error)) {
        return 'unexpectedError'
      }

      //https://github.com/aws-amplify/amplify-js/blob/75e1c72bf92fe8ae32cdf239d5f4b89d6fd4432a/packages/auth/src/providers/cognito/types/errors.ts
      switch (e.name) {
        case 'CodeMismatchException':
          return 'codeMismatchError'
        case 'ExpiredCodeException':
          return 'expiredCodeError'
        case 'MFAMethodNotFoundException':
          return 'MFAMethodNotFoundError'
        case 'EnableSoftwareTokenMFAException':
          return 'enableSoftwareTokenMFAError'
        case 'SoftwareTokenMFANotFoundException':
          return 'softwareTokenMFANotFoundError'
        case 'ConcurrentModificationException':
          return 'concurrentModificationError'
        case 'PasswordResetRequiredException':
          return 'passwordResetRequired'
        case 'TooManyRequestsException':
          return 'tooManyRequestsError'
        case 'NotAuthorizedException':
          return 'userNotAuthorizedError'

        default:
          return 'unexpectedError'
      }
    }
  }

  async function logout(): Promise<void> {
    console.log('logging out')
    loginStage.value = LoginStage.LoggingOut
    try {
      if (isMockMode) {
        await mockAuthService.signOut()
      } else {
        await signOut()
      }
    } finally {
      user.value = null
      loginStage.value = LoginStage.AwaitingLogin
    }
  }

  async function resetPass(parameters: ResetPasswordInput): Promise<ResetPasswordResult> {
    try {
      const { nextStep } = await resetPassword(parameters)
      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        console.log(nextStep)
        if (nextStep.codeDeliveryDetails.deliveryMedium === 'SMS') {
          return 'enterCodeSMS'
        } else if (nextStep.codeDeliveryDetails.deliveryMedium === 'EMAIL') {
          return 'enterCodeEmail'
        }
        return 'unexpectedError'
      } else {
        return 'done'
      }
    } catch (e: unknown) {
      if (!(e instanceof Error)) {
        return 'unexpectedError'
      }

      //https://github.com/aws-amplify/amplify-js/blob/75e1c72bf92fe8ae32cdf239d5f4b89d6fd4432a/packages/auth/src/providers/cognito/types/errors.ts
      switch (e.name) {
        case 'CodeDeliveryFailureException':
          return 'codeDeliveryFailure'
        case 'ForbiddenException':
          return 'forbidden'
        case 'LimitExceededException':
          return 'limitExceeded'
        case 'TooManyRequestsException':
          return 'tooManyRequests'
        default:
          return 'unexpectedError'
      }
    }
  }

  async function confirmResetPass(parameters: ConfirmResetPasswordInput): Promise<ConfirmResetPasswordResult> {
    try {
      await confirmResetPassword(parameters)
      return 'passwordReset'
    } catch (e: unknown) {
      if (!(e instanceof Error)) {
        return 'unexpectedError'
      }

      //https://github.com/aws-amplify/amplify-js/blob/75e1c72bf92fe8ae32cdf239d5f4b89d6fd4432a/packages/auth/src/providers/cognito/types/errors.ts
      switch (e.name) {
        case 'CodeMismatchException':
          return 'codeMismatch'
        case 'ExpiredCodeException':
          return 'expiredCode'
        case 'ForbiddenException':
          return 'forbidden'
        case 'InvalidPasswordException':
          return 'invalidPassword'
        case 'LimitExceededException':
          return 'limitExceeded'
        case 'TooManyFailedAttemptsException':
          return 'tooManyFailedAttempts'
        case 'TooManyRequestsException':
          return 'tooManyRequests'
        default:
          return 'unexpectedError'
      }
    }
  }

  const isAuthenticated = computed(() => loginStage.value === LoginStage.Authenticated)

  return {
    loginStage: readonly(loginStage),
    nextAction: readonly(nextAction),
    user: readonly(user),
    isAuthenticated,
    initializeAuth,
    login,
    loginNextStep,
    logout,
    resetPassword: resetPass,
    confirmResetPassword: confirmResetPass,
  }
})
